import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type CustomRole,
  type MatrixRole,
  type MemberRole,
  MEMBER_ROLES,
  PermissionChecklist,
  type PermissionMatrix,
  useCreateCustomRole,
  useCustomRoles,
  useDeleteCustomRole,
  usePermissionMatrix,
  useSetRolePermissions,
  useUpdateCustomRole,
  useWorkspaceMembers,
} from '@/entities/collab';
import type { Workspace } from '@/entities/workspace';
import { FormError } from '@/shared/forms';
import { can, PERMISSIONS } from '@/shared/lib/permissions';
import {
  Button,
  Combobox,
  Dialog,
  ErrorState,
  HelpRow,
  Icons,
  LoadingState,
  TextAreaField,
  TextField,
  useToast,
} from '@/shared/ui';

import styles from './Admin.module.css';
import { HierarchyEditor, LevelSelect } from './HierarchyEditor';

/** What the catalog shows for one role (a standard or a custom one). */
interface CatalogEntry {
  key: string;
  kind: 'standard' | 'custom';
  name: string;
  description: string;
  members: number;
  role?: MemberRole;
  custom?: CustomRole;
  color?: string | null;
  protectedRole: boolean;
}

/**
 * Roles and permissions. The default view is a compact CATALOG of every role (standard and
 * custom, searchable) - never the whole permission matrix at once. Opening a role shows its
 * editor: the grouped permission checklist with Save / Reset.
 */

/** Owner-only permissions, plus the owner-assigned ones (confidential databases) for anybody
 * but the owner: the server refuses those changes from everyone else. */
function lockedFor(matrix: PermissionMatrix, isOwner: boolean): Set<string> {
  return new Set([...matrix.owner_only, ...(isOwner ? [] : (matrix.owner_assigned ?? []))]);
}

export function RolesTab({ workspace }: { workspace: Workspace }) {
  const { t } = useTranslation();
  const canEdit = can(workspace.permissions, PERMISSIONS.workspaceManageCustomRoles);
  const roles = useCustomRoles(workspace.id);
  const members = useWorkspaceMembers(workspace.id);
  const matrix = usePermissionMatrix(workspace.id);
  const [search, setSearch] = useState('');
  const [opened, setOpened] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [view, setView] = useState<'catalog' | 'hierarchy'>('catalog');
  const [deleting, setDeleting] = useState<CustomRole | null>(null);

  const entries = useMemo<CatalogEntry[]>(() => {
    const counts = new Map<string, number>();
    for (const member of members.data ?? []) {
      const key = member.role ?? '';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    const standard: CatalogEntry[] = MEMBER_ROLES.map((role) => ({
      key: `std:${role}`,
      kind: 'standard',
      name: t(`roles.${role}`),
      description: t(`roles.description.${role}`),
      members: counts.get(role) ?? 0,
      role,
      protectedRole: role === 'owner',
    }));
    const custom: CatalogEntry[] = (roles.data ?? []).map((role) => ({
      key: `custom:${role.id}`,
      kind: 'custom',
      name: role.name,
      description: role.description ?? '',
      members: role.member_count,
      custom: role,
      color: role.color,
      protectedRole: false,
    }));
    return [...standard, ...custom];
  }, [members.data, roles.data, t]);

  if (roles.isPending || matrix.isPending) return <LoadingState />;
  if (roles.isError) return <ErrorState error={roles.error} />;
  if (matrix.isError) return <ErrorState error={matrix.error} />;

  const needle = search.trim().toLocaleLowerCase();
  const shown = entries.filter(
    (entry) =>
      needle === '' ||
      entry.name.toLocaleLowerCase().includes(needle) ||
      entry.description.toLocaleLowerCase().includes(needle),
  );
  const current = entries.find((entry) => entry.key === opened) ?? null;

  if (current) {
    return (
      <div className={styles.panel}>
        <div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setOpened(null);
            }}
          >
            <Icons.chevronLeft /> {t('workspaceAdmin.backToRoles')}
          </Button>
        </div>
        {current.kind === 'standard' && current.role ? (
          <StandardRoleEditor
            workspace={workspace}
            matrix={matrix.data}
            entry={current}
            canEdit={canEdit}
          />
        ) : current.custom ? (
          <CustomRoleEditor
            key={current.custom.id}
            workspace={workspace}
            matrix={matrix.data}
            role={current.custom}
            canEdit={canEdit}
            onDelete={() => {
              setDeleting(current.custom ?? null);
            }}
          />
        ) : null}
        {deleting && (
          <DeleteRoleDialog
            workspaceId={workspace.id}
            role={deleting}
            others={roles.data.filter((r) => r.id !== deleting.id)}
            onClose={() => {
              setDeleting(null);
            }}
            onDeleted={() => {
              setDeleting(null);
              setOpened(null);
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <HelpRow topic={t('workspaceAdmin.tabs.roles')} topicId="roles">
        <div className={styles.segmented} role="group" aria-label={t('workspaceAdmin.tabs.roles')}>
          {(['catalog', 'hierarchy'] as const).map((id) => (
            <button
              key={id}
              type="button"
              className={view === id ? styles.segmentOn : styles.segment}
              aria-pressed={view === id}
              onClick={() => {
                setView(id);
              }}
            >
              {t(`workspaceAdmin.rolesView.${id}`)}
            </button>
          ))}
        </div>
      </HelpRow>
      {view === 'hierarchy' ? (
        <HierarchyEditor workspaceId={workspace.id} roles={roles.data} canEdit={canEdit} />
      ) : (
        <>
          <div className={styles.toolbar}>
            <input
              type="search"
              className={styles.field}
              value={search}
              placeholder={t('workspaceAdmin.searchRoles')}
              aria-label={t('workspaceAdmin.searchRoles')}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
            {canEdit && (
              <Button
                variant="primary"
                onClick={() => {
                  setCreating(true);
                }}
              >
                <Icons.plus /> {t('workspaceAdmin.addRole')}
              </Button>
            )}
          </div>
          {shown.length === 0 && <p className={styles.note}>{t('common.noResults')}</p>}
          <ul className={styles.catalog} aria-label={t('workspaceAdmin.tabs.roles')}>
            {shown.map((entry) => (
              <li key={entry.key} className={styles.roleCard} data-testid="role-card">
                <button
                  type="button"
                  className={styles.roleOpen}
                  onClick={() => {
                    setOpened(entry.key);
                  }}
                >
                  <span className={styles.roleHead}>
                    {entry.color && (
                      <span className={styles.swatch} style={{ background: entry.color }} />
                    )}
                    <span className={styles.roleName}>{entry.name}</span>
                    <span className={styles.chip}>
                      {entry.protectedRole
                        ? t('workspaceAdmin.roleProtected')
                        : entry.kind === 'standard'
                          ? t('workspaceAdmin.roleStandard')
                          : t('workspaceAdmin.roleCustom')}
                    </span>
                  </span>
                  {entry.description && <span className={styles.meta}>{entry.description}</span>}
                  <span className={styles.meta}>
                    {t('workspaceAdmin.roleMembers', { count: entry.members })}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <p className={styles.note}>{t('workspaceAdmin.rolesBoardNote')}</p>
        </>
      )}
      {creating && (
        <CreateRoleDialog
          workspace={workspace}
          matrix={matrix.data}
          onClose={() => {
            setCreating(false);
          }}
        />
      )}
    </div>
  );
}

/** Permission checklist of ONE standard role of the workspace matrix. */
function StandardRoleEditor({
  workspace,
  matrix,
  entry,
  canEdit,
}: {
  workspace: Workspace;
  matrix: PermissionMatrix;
  entry: CatalogEntry;
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const save = useSetRolePermissions(workspace.id);
  const role = entry.role as MemberRole;
  const stored: MatrixRole | undefined = matrix.roles.find((r) => r.role === role);
  const [edited, setEdited] = useState<Set<string> | null>(null);
  const draft = edited ?? new Set(stored?.permissions ?? []);
  const editable = (stored?.editable ?? false) && canEdit;
  const dirty =
    stored !== undefined &&
    edited !== null &&
    (stored.permissions.length !== edited.size || stored.permissions.some((p) => !edited.has(p)));
  const mine = new Set(workspace.permissions);
  return (
    <section className={styles.card} aria-label={entry.name}>
      <div className={styles.cardHead}>
        <h3 className={styles.subheading}>{entry.name}</h3>
        <span className={styles.chip}>
          {entry.protectedRole
            ? t('workspaceAdmin.roleProtected')
            : t('workspaceAdmin.roleStandard')}
        </span>
      </div>
      <p className={styles.note}>
        {entry.protectedRole ? t('workspaceAdmin.ownerRoleNote') : t('workspaceAdmin.matrixHint')}
      </p>
      <PermissionChecklist
        groups={matrix.groups}
        value={draft}
        onChange={setEdited}
        required={new Set(matrix.required)}
        locked={lockedFor(matrix, workspace.is_owner)}
        defaults={new Set(stored?.defaults ?? [])}
        disabled={!editable}
        grantable={workspace.is_owner ? null : mine}
      />
      <p className={styles.note}>{t('workspaceAdmin.nondelegableNote')}</p>
      <FormError error={save.error} />
      {editable && (
        <div className={styles.actions}>
          <Button
            disabled={!dirty && edited === null}
            onClick={() => {
              setEdited(null);
            }}
          >
            {t('workspaceAdmin.revertChanges')}
          </Button>
          <Button
            onClick={() => {
              setEdited(new Set(stored?.defaults ?? []));
            }}
          >
            {t('workspaceAdmin.resetDefaults')}
          </Button>
          <Button
            variant="primary"
            disabled={!dirty || save.isPending}
            onClick={() => {
              save.mutate(
                { role, permissions: [...draft] },
                {
                  onSuccess: () => {
                    setEdited(null);
                    toast.show({ message: t('workspaceAdmin.saved') });
                  },
                },
              );
            }}
          >
            {t('common.save')}
          </Button>
        </div>
      )}
    </section>
  );
}

function sameSet(a: ReadonlySet<string>, b: readonly string[]): boolean {
  return a.size === b.length && b.every((x) => a.has(x));
}

/** A custom role: name, description, seniority and its two checklists. */
function CustomRoleEditor({
  workspace,
  matrix,
  role,
  canEdit,
  onDelete,
}: {
  workspace: Workspace;
  matrix: PermissionMatrix;
  role: CustomRole;
  canEdit: boolean;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const update = useUpdateCustomRole(workspace.id);
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description ?? '');
  const [rank, setRank] = useState(String(role.rank));
  const [permissions, setPermissions] = useState(new Set(role.permissions));
  const [boardPermissions, setBoardPermissions] = useState(new Set(role.board_permissions));
  const workspaceGroups = Object.fromEntries(
    Object.entries(matrix.groups).filter(([group]) => group === 'workspace'),
  );
  const boardGroups = Object.fromEntries(
    Object.entries(matrix.groups).filter(([group]) => group !== 'workspace'),
  );
  const dirty =
    name.trim() !== role.name ||
    description !== (role.description ?? '') ||
    Number(rank) !== role.rank ||
    !sameSet(permissions, role.permissions) ||
    !sameSet(boardPermissions, role.board_permissions);
  const grantable = workspace.is_owner ? null : new Set(workspace.permissions);
  return (
    <section className={styles.card} aria-label={role.name}>
      <div className={styles.cardHead}>
        <h3 className={styles.subheading}>{role.name}</h3>
        <span className={styles.chip}>{t('workspaceAdmin.roleCustom')}</span>
      </div>
      <TextField
        label={t('workspaceAdmin.roleName')}
        value={name}
        maxLength={60}
        disabled={!canEdit}
        onChange={(event) => {
          setName(event.target.value);
        }}
      />
      <TextAreaField
        label={t('workspaceAdmin.description')}
        value={description}
        maxLength={500}
        disabled={!canEdit}
        onChange={(event) => {
          setDescription(event.target.value);
        }}
      />
      <LevelSelect
        workspaceId={workspace.id}
        value={Number(rank)}
        disabled={!canEdit}
        onChange={(next) => {
          setRank(String(next));
        }}
      />
      <h4 className={styles.subheading}>{t('workspaceAdmin.roleWorkspacePermissions')}</h4>
      <PermissionChecklist
        groups={workspaceGroups}
        value={permissions}
        onChange={setPermissions}
        required={new Set(['workspace.view'])}
        locked={lockedFor(matrix, workspace.is_owner)}
        disabled={!canEdit}
        grantable={grantable}
      />
      <h4 className={styles.subheading}>{t('workspaceAdmin.roleBoardDefaults')}</h4>
      <p className={styles.note}>{t('workspaceAdmin.roleBoardDefaultsHint')}</p>
      <PermissionChecklist
        groups={boardGroups}
        scope={(permission) => !permission.startsWith('workspace.')}
        value={boardPermissions}
        onChange={setBoardPermissions}
        disabled={!canEdit}
        grantable={grantable}
      />
      <FormError error={update.error} />
      {canEdit && (
        <div className={styles.actions}>
          <Button variant="danger" onClick={onDelete}>
            {t('common.delete')}
          </Button>
          <Button
            disabled={!dirty}
            onClick={() => {
              setName(role.name);
              setDescription(role.description ?? '');
              setRank(String(role.rank));
              setPermissions(new Set(role.permissions));
              setBoardPermissions(new Set(role.board_permissions));
            }}
          >
            {t('workspaceAdmin.revertChanges')}
          </Button>
          <Button
            variant="primary"
            disabled={!dirty || update.isPending || !name.trim()}
            onClick={() => {
              update.mutate(
                {
                  roleId: role.id,
                  name: name.trim(),
                  description,
                  rank: Number(rank),
                  permissions: [...permissions],
                  board_permissions: [...boardPermissions],
                },
                {
                  onSuccess: () => {
                    toast.show({ message: t('workspaceAdmin.saved') });
                  },
                },
              );
            }}
          >
            {t('common.save')}
          </Button>
        </div>
      )}
    </section>
  );
}

function CreateRoleDialog({
  workspace,
  matrix,
  onClose,
}: {
  workspace: Workspace;
  matrix: PermissionMatrix;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreateCustomRole(workspace.id);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [rank, setRank] = useState('0');
  const [permissions, setPermissions] = useState<Set<string>>(new Set(['workspace.view']));
  const workspaceGroups = Object.fromEntries(
    Object.entries(matrix.groups).filter(([group]) => group === 'workspace'),
  );
  return (
    <Dialog title={t('workspaceAdmin.addRole')} size="lg" onClose={onClose}>
      <form
        className={styles.panel}
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate(
            {
              name: name.trim(),
              description,
              rank: Number(rank),
              permissions: [...permissions],
              board_permissions: [],
            },
            { onSuccess: onClose },
          );
        }}
      >
        <TextField
          label={t('workspaceAdmin.roleName')}
          value={name}
          maxLength={60}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <TextAreaField
          label={t('workspaceAdmin.description')}
          value={description}
          maxLength={500}
          onChange={(event) => {
            setDescription(event.target.value);
          }}
        />
        <LevelSelect
          workspaceId={workspace.id}
          value={Number(rank)}
          onChange={(next) => {
            setRank(String(next));
          }}
        />
        <PermissionChecklist
          groups={workspaceGroups}
          value={permissions}
          onChange={setPermissions}
          required={new Set(['workspace.view'])}
          locked={lockedFor(matrix, workspace.is_owner)}
          grantable={workspace.is_owner ? null : new Set(workspace.permissions)}
        />
        <FormError error={create.error} />
        <div className={styles.actions}>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" variant="primary" disabled={create.isPending || !name.trim()}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function DeleteRoleDialog({
  workspaceId,
  role,
  others,
  onClose,
  onDeleted,
}: {
  workspaceId: string;
  role: CustomRole;
  others: readonly CustomRole[];
  onClose: () => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const remove = useDeleteCustomRole(workspaceId);
  const [reassign, setReassign] = useState('');
  return (
    <Dialog title={t('workspaceAdmin.deleteRoleTitle', { name: role.name })} onClose={onClose}>
      {role.member_count > 0 ? (
        <>
          <p>{t('workspaceAdmin.deleteRoleHolders', { count: role.member_count })}</p>
          <Combobox
            label={t('workspaceAdmin.reassignTo')}
            value={reassign || '__none__'}
            searchPlaceholder={t('common.search')}
            emptyText={t('common.noResults')}
            options={[
              { value: '__none__', label: t('workspaceAdmin.reassignNone') },
              ...others.map((r) => ({ value: r.id, label: r.name })),
            ]}
            onChange={(next) => {
              setReassign(next === '__none__' ? '' : next);
            }}
          />
        </>
      ) : (
        <p>{t('workspaceAdmin.deleteRoleMessage')}</p>
      )}
      <FormError error={remove.error} />
      <div className={styles.actions}>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="danger"
          disabled={remove.isPending}
          onClick={() => {
            remove.mutate(
              {
                roleId: role.id,
                ...(reassign ? { reassign_to_role_id: reassign } : { clear: true }),
              },
              { onSuccess: onDeleted },
            );
          }}
        >
          {t('common.delete')}
        </Button>
      </div>
    </Dialog>
  );
}
